/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import findLast from 'lodash/findLast';

import {
  AudienceType,
  DocVersionType,
  DocumentVersionLockType,
  DocumentVersionSearchType,
  DocumentVersionState,
  DocumentVersionType,
  HowActivatedType,
  MessageType,
  OrganizationType,
  PageFhirType,
  PageTypeEnum,
  PageType,
  ReturnToHereInfo,
  RoleType,
  SelectedButtonInfo,
  DocumentMetadataType,
  DocumentVersionWithoutGeneratedFieldsType,
} from '../algorithm/types';
import { FhirState, fetchPredefinedWidgetsThunk } from '../Fhir/fhirSlice';
import {
  audienceToUrlParam,
  createRandomId,
  hashCode,
  isPrePublished,
  isPublishedVersion,
  isQuillContentEmpty,
} from '../utils';
import { findKeys } from '../algorithm/swimlane_utils';
import { RootState } from '../../../app/store';
import { IPearlLibrary, selectSearchSourceType, UserState } from '../Login/userSlice';
// eslint-disable-next-line import/no-restricted-paths
import { addCreatorExtraReducers } from '../../Doc/creatorChatSlice';
import { convertPearlApiDocumentVersionToDocumentVersionType } from './convertPearlApiDocumentVersionToDocumentVersion';
import { documentVersionUpdateGeneratedFields } from './documentVersionGeneratedFieldsUtils';
import pearlApi from '../api/pearlApi';
import { snackNotifications } from '../notification/SnackBarUtils';
import {
  addPageToMessages,
  addChatMessage,
  confirmMessageNext as confirmMessageNext_,
  rollbackToMessageHelper,
  toggleOptionKey as toggleOptionKey_,
  RunAlgorithmCallbacks,
  addToPagesUsedHelper,
  rollbackAndRunPage,
} from '../algorithm/runAlgorithm';
import { NO_SERVER } from '../api/api_config';
import { fixupDocumentVersion } from './fixupDocumentAndPages';
import { enableNextButton } from '../permissions';
// eslint-disable-next-line import/no-restricted-paths
import { fetchDocumentForExternalReviewThunk } from '../../../ExternalReviewer/externalReviewSlice';

/* eslint-disable @typescript-eslint/no-unused-vars */

type DocumentVersionLoading = { isLoading: boolean; notFound: boolean; error?: string; lastChecked: number };

/** If id is undefined, then it's a new page */
export type UnsavedPageChangeType = Partial<PageType> & {
  id: string | undefined;
  document_version_id: string;
};
export type UnsavedPageChangeWithoutFhirType = UnsavedPageChangeType & { fhir?: undefined };

export type SearchMetadata = {
  index?: string;
  queryID?: string;
  objectIDs?: string[];
  positions?: number[];
};

/**
 * I've put a lot of thought into how to structure pages, documents and documentVersions here.
 */
export interface ChatState {
  creatorMode: boolean;
  messages: MessageType[];
  messageFlipped?: {
    index: number; // Which message in messages that we are currently editing
    page?: PageType; // Which page inside the flipped message we are currently editing.  Can be undefined, to mean just the first page
    swimlaneIndex?: number; // If we are editing a swimlane, which swimlane to edit
  };
  organizations: { [id: string]: OrganizationType };
  organizationsLoading: { [id: string]: true };
  documentVersionsLoading: { [documentVersionId: string]: DocumentVersionLoading };
  documentVersions: { [documentVersionId: string]: DocumentVersionType };
  publishedDocuments: {
    [documentId: string]: (DocumentVersionType & { state: DocumentVersionState.PUBLISHED }) | null;
  };
  prepublishedDocuments: { [documentId: string]: DocumentVersionType | null };
  documentVersionLocks: { [documentVersionId: string]: DocumentVersionLockType };
  selected: { [key: string]: SelectedButtonInfo };
  returnToHere: { messageIndex: number; info: ReturnToHereInfo }[];
  _unsavedPageChanges: { [id: string]: UnsavedPageChangeType }; // This is a partial page - only the fields that have changed. I've added an underscore to discourage direct access
  unsavedPages: { [id: string]: PageType }; // This is a CACHED combined version of _unsavedPageChanges and documentVersions. DO NOT MODIFY DIRECTLY except only to combine _unsavedPageChanges and documentVersions  isSaving: boolean;
  isSaving: boolean;
  isSearching: number; // If >0, then textInputThunk is running.  Could be user typed in search prompt, or url location changed (e.g. link clicked)
  tabPageSelected?: { documentVersionId: string; tabPageId: string; link: string; tabValue: string };
  currentSearch: TextInputThunkArgs | undefined;
  // Which pages are being used - for highlighting in the algorithm editor
  pagesUsed: { [id: string]: true };
  publishedOnly: boolean; // Whether we are showing only published documents
  searchMetadata?: SearchMetadata;
  dateRange?: number;
  queryMeds?: string[];
}

interface ItemWithID {
  id: string;
}
export function listToIdDict<Type extends ItemWithID>(list: Type[]): { [id: string]: Type } {
  const idDict: { [id: string]: Type } = {};
  list.forEach((item: Type) => {
    if (item.id in idDict) {
      console.error('Two items with the same id:', item.id);
      throw new Error('Two items with the same id:');
    }
    idDict[item.id] = item;
  });
  return idDict;
}
interface ItemWithNameAndId {
  id: string;
  name: string;
}
export function listToNameIdDict<Type extends ItemWithNameAndId>(list: Type[]): { [name: string]: string } {
  const nameDict: { [id: string]: string } = {};
  list.forEach((item: Type) => {
    if (item.name in nameDict) {
      console.error('Two items with the same name:', nameDict[item.name], item);
      throw new Error('Two items with the same name:');
    }
    nameDict[item.name] = item.id;
  });
  return nameDict;
}

const WELCOME_MESSAGE: MessageType & { extra: { type: 'WELCOME_MESSAGE' } } = {
  is_sent: false,
  no_bubble: true,
  extra: {
    type: 'WELCOME_MESSAGE',
  },
  index: 0,
} as const;

export const initialState: ChatState = {
  creatorMode: false,
  messages: [],
  messageFlipped: undefined,
  organizations: {},
  organizationsLoading: {},
  documentVersionsLoading: {},
  documentVersions: {},
  publishedDocuments: {},
  prepublishedDocuments: {},
  documentVersionLocks: {},
  selected: {},
  _unsavedPageChanges: {},
  unsavedPages: {},
  isSaving: false,
  isSearching: 0,
  currentSearch: undefined,
  returnToHere: [],
  pagesUsed: {},
  publishedOnly: true,
  searchMetadata: {},
  queryMeds: [],
};

/* Some localized selectors that use the ChatState and memoize the dicts.  Just to improve performance.
   Do not export these.  For use in the reducer only */
const selectPageByIdLocalized = (state: ChatState, documentVersionId: string, id: string): PageType | undefined =>
  state.documentVersions[documentVersionId]?.pages[id];
const selectDocumentVersionsDictLocalized = (state: ChatState) => state.documentVersions;

const selectPublishedDocuments = (state: RootState) => state.chat.publishedDocuments;

// FIXME: This is a temporary hack, until we have proper tag searching on the backend.  Then this
// will become a network call instead.
const selectTagToPublishedDocumentVersionMapLocalized = createSelector(
  [selectDocumentVersionsDictLocalized],
  (documentVersions) =>
    Object.values(documentVersions)
      .filter((documentVersion) => documentVersion.state === DocumentVersionState.PUBLISHED)
      .reduce((map, documentVersion) => {
        documentVersion.tags.forEach((tag) => (map[tag] = documentVersion));
        documentVersion.docCategories.forEach((tag) => (map[tag.id] = documentVersion));
        return map;
      }, {} as { [tag: string]: DocumentVersionType })
);
const selectTagToUnpublishedDocumentVersionMapLocalized = createSelector(
  [selectDocumentVersionsDictLocalized],
  (documentVersions) =>
    Object.values(documentVersions)
      .filter(
        (documentVersion) =>
          documentVersion.state === DocumentVersionState.DRAFT || documentVersion.state === DocumentVersionState.REVIEW
      )
      .reduce((map, documentVersion) => {
        documentVersion.tags.forEach((tag) => (map[tag] = documentVersion));
        documentVersion.docCategories.forEach((tag) => (map[tag.id] = documentVersion));
        return map;
      }, {} as { [tag: string]: DocumentVersionType })
);

// FIXME: This is a temporary hack, until we have proper tag searching on the backend.  Then this
// will become a network call instead.
const selectTagToDocumentVersionLocalized = (state: ChatState, tag: string, publishedOnly: boolean) =>
  (!publishedOnly && selectTagToUnpublishedDocumentVersionMapLocalized(state)[tag]) ||
  selectTagToPublishedDocumentVersionMapLocalized(state)[tag];

/** User has interacted with a previous message.
 *  Remove all future messages, but also rollback the selected state.
 *  This is for internal use only, and should not be called directly.
 *  Instead call rollbackAndRunPage, which will call this function
 *  and then run the first page on the message.
 */
function rollbackToMessage(
  state: ChatState,
  index: number,
  pageId?: string | undefined,
  clearSelected: boolean = false
) {
  delete state.messageFlipped;
  state._unsavedPageChanges = {};
  state.unsavedPages = {};
  state.currentSearch = undefined; // We also need to clear the current search
  const { messages, selected, returnToHere, pagesUsed } = rollbackToMessageHelper(
    index,
    pageId,
    state.messages,
    state.returnToHere,
    clearSelected
  );
  state.returnToHere = returnToHere;
  state.messages = messages;
  state.selected = selected;
  state.pagesUsed = pagesUsed;
  return { messages, selected };
}

/** Return type from GET /documentVersions/:documentVersionId  and GET /documentVersions/search when there is only one result (if we specified an audience)
 *  Note that GET /documentVersions/search may also return PearlApiDocumentVersionSearchType.
 *
 *
 *  Note if you want to add to this, you probably also want to add to @see PageBaseType or similar
 *  @see PearlApiDocumentVersionSearchType for when there is multiple results from a search
 *  @see https://pearl-project.atlassian.net/wiki/spaces/PearlArchitecture/pages/9371649/Pearl+API+Specifications#Search---Consumer
 *  @see https://pearl-project.atlassian.net/wiki/spaces/PearlArchitecture/pages/9371649/Pearl+API+Specifications#Get-a-document-version%2C-root-pages-and-all-pages
 */

export type PearlCategoryType = {
  title: string;
  audience: AudienceType;
};

export type PearlApiAudienceType = {
  persona: string;
  care_category: string;
  patient_group: string;
};

export type PearlApiDocumentVersionType = {
  createdAt: string;
  document?: {
    docType: DocVersionType;
    id: string;
    members?: { userId?: string; name?: string; email?: string; role: RoleType }[];
    library: IPearlLibrary;
    slug: string;
    doc_group?: { id: string; name: string };
  };
  hasGuides?: boolean | null;
  hasAlgorithm?: boolean | null;
  id: string;
  assigneeId?: string | null;
  pages: {
    id: string;
    content: {
      pageType: PageTypeEnum;
      [key: string]: string | number;
    };
    createdAt: string;
    isGuide: boolean;
    isRoot: boolean;
    name: string;
    updatedAt: string;
    fhir?: PageFhirType;
    meta?: {
      labels?: { [key: string]: string };
    };
  }[];
  state: DocumentVersionState;
  title: string;
  snapshotName?: string;
  createdFromId?: string; // documentVersionId of the documentVersion that this was created from
  topic_subtopic_pairs: { topic: string; subtopic: string }[];
  nextReviewAt: string | null;
  updatedAt: string;
  tags: string[];
  flags?: string[];
  has_comments_needing_attention?: boolean;
  docCredits?: { id?: string; title: string; name: string }[];
  docCategories?: { id: string; name: string }[];
  governanceModel?: { id: string; name: string } | null;
  likedByYou?: boolean;
  bookmarkedByYou?: boolean;
  likesCount?: number;
  // New way we do audience
  audience: {
    care_categories: { id: string; name: string }[];
    patient_groups: { id: string; name: string }[];
    personas: { id: string; name: string }[];
  };
  library: IPearlLibrary;
  metadata?: DocumentMetadataType; // Only in autocomplete_rt endpoint
  doc_slug?: string; // Only in autocomplete_rt endpoint - otherwise use document.slug
  document_version_id?: string; // Only in autocomplete_rt endpoint
};

export type SlugInfoType = {
  slug?: string;
  oldDocName?: string;
  currentDocName?: string;
  pageName?: string;
  docId?: string;
  sameDocument?: boolean; // Whether the link is to the same document
};

/** Return type from GET /documentVersions/search when there is more than one result. (or one result but no audience specified)
 * @see PearlApiDocumentVersionType
 */
export type PearlApiDocumentVersionSearchType = {
  id: string;
  state: DocumentVersionState;
  title: string;
  document?: {
    docType: DocVersionType;
    id: string;
    library: IPearlLibrary;
    slug: string;
  };
  createdAt: string;
  updatedAt: string;
  likedByYou: boolean;
  bookmarkedByYou?: boolean;
  likesCount: number;
  flags?: string[];
  has_comments_needing_attention?: boolean;
  audience: {
    // New way we do audience
    care_categories: { id: string; name: string }[];
    patient_groups: { id: string; name: string }[];
    personas: { id: string; name: string }[];
  };
  metadata?: DocumentMetadataType; //metadata contains triageBoost, based on search results will be prioritized
  doc_slug?: string; //doc_slug is from the autocomplete_rt endpoint
  document_versionId?: string; // from autocomplete_rt endpoint
  library: IPearlLibrary;
};

export function fixupGuideName(guideName: string) {
  // Fix guideName for tabs (capitalization and for tab view) without having to rename in the backend and messing up pendo
  return guideName === 'Clinical notes'
    ? `Clinical\n Notes`
    : guideName === 'Orders To Consider'
    ? `Orders\n to Consider`
    : guideName;
}

export const standardGuides = Object.freeze([
  'Clinical notes' /** Note we rename to 'Clinical Notes' when we display it - @see fixupGuideName() */,
  'Orders To Consider',
  'Referral Scheduling Recommendations',
  'Resources',
]);

export const standardGuidesSet = new Set(standardGuides);

export function defaultNewPage(isRoot: boolean, document_version_id: string, name?: string): PageType {
  return {
    id: '',
    pageType: PageTypeEnum.MULTIPLE_QUESTIONS,
    name: name || '',
    document_version_id: document_version_id,
    content: '',
    isRoot,
    questions: [],
  };
}

// Exported for testing
export function isPearlApiDocumentVersionType(
  data: PearlApiDocumentVersionType | PearlApiDocumentVersionSearchType
): data is PearlApiDocumentVersionType {
  return !!data.document && 'members' in data.document && 'pages' in data;
}

// Exported for testing
export function isDocumentVersionType(
  data: DocumentVersionType | DocumentVersionSearchType
): data is DocumentVersionType {
  return !!data.document && 'members' in data.document && 'pages' in data;
}

export type SearchDocumentVersionType = (DocumentVersionType | DocumentVersionSearchType)[];

export async function fetchPearlApiDocumentVersionById(
  documentVersionId: string | undefined,
  documentId?: string | undefined
) {
  let data: PearlApiDocumentVersionType | undefined;
  if (documentVersionId) {
    data = (await pearlApi.get(`/documentVersions/${documentVersionId}?include=allPages`)).data.data;
  } else if (documentId) {
    data = (await pearlApi.get(`/document/${documentId}/published?include=allPages`)).data.data;
  } else {
    throw new Error('Missing both document version id and document id');
  }
  return data;
}

export async function fetchDocumentVersionById(documentVersionId: string | undefined, documentId?: string | undefined) {
  const data = await fetchPearlApiDocumentVersionById(documentVersionId, documentId);
  return data ? await convertPearlApiDocumentVersionToDocumentVersionType(data) : undefined;
}

export async function fetchDocumentVersionBySlug(slug: string, publishedOnly: boolean) {
  const prePublishedStr = publishedOnly ? '' : 'prePublished,';
  const data = (await pearlApi.get(`/document/${slug}/published?include=${prePublishedStr}allPages`)).data.data;
  return data ? await convertPearlApiDocumentVersionToDocumentVersionType(data) : undefined;
}

export const fetchDocumentVersionThunk = createAsyncThunk<
  DocumentVersionType | undefined,
  | {
      documentVersionId: string;
      documentId?: undefined;
      slug?: undefined;
      publishedOnly?: false;
      documentVersion?: undefined;
    }
  | {
      documentVersionId?: undefined;
      documentId: string;
      slug?: undefined;
      publishedOnly: boolean;
      documentVersion?: undefined;
    }
  | {
      documentVersionId?: undefined;
      documentId: string | undefined; // We can still use the documentId to track when we can't find the document, but the slug is used for the search
      slug: string;
      publishedOnly: boolean;
      documentVersion?: undefined;
    }
  | {
      documentVersionId?: undefined;
      documentId?: undefined;
      slug?: undefined;
      publishedOnly?: undefined;
      documentVersion: DocumentVersionWithoutGeneratedFieldsType;
    },
  { state: { chat: ChatState } }
>(
  'chat/fetchDocumentVersion',
  async ({ documentVersionId, documentId, slug, documentVersion, publishedOnly }, thunkAPI) => {
    // We call fetchDocumentVersionThunk when we need to check if there are any updates to the document, and in those circumstances we don't
    // want to show the loading screen.  So don't show the loading screen in that case.
    // Otherwise it makes it impossible to flip a chat to edit, and have it check for updates.

    try {
      if (documentVersion) {
        // This is mostly for testing, where we want to pass in a documentVersion directly instead of fetching it
        // Make a deep copy of documentVersion
        const documentVersion_ = JSON.parse(JSON.stringify(documentVersion)) as DocumentVersionType;
        documentVersionUpdateGeneratedFields(documentVersion_);
        return documentVersion_;
      } else if (slug) {
        return await fetchDocumentVersionBySlug(slug, publishedOnly ?? thunkAPI.getState().chat.publishedOnly);
      } else {
        return await fetchDocumentVersionById(documentVersionId, documentId);
      }
    } catch (error) {
      const status = (error as any).response?.status;
      if (status !== 404 && status !== 400) {
        // 404 means the document doesn't have a published version, 400 means document doesn't exist
        snackNotifications.error(`Failed to fetch information about document: ${(error as Error).message}`);
        throw error;
      }
      // For 404, don't throw an error, but just return undefined to indicate that the document doesn't exist
      return undefined;
    }
  }
);

/**
 * Opens the document with the given documentVersionId or documentId.
 *
 * This shouldn't be called directly from anywhere except ChatComponent, and instead
 * @see setDocumentToSearchFor should be called.  This is to avoid various race conditions.
 *
 */
export const openDocumentByIdThunk = createAsyncThunk<
  DocumentVersionType | undefined, // return type
  {
    // parameters type
    documentVersionId?: string;
    documentId?: string;
    documentTitle?: string;
    page?: string;
    slug?: string;
    audienceFilter: AudienceType;
    publishedOnly: boolean;
  },
  { state: { chat: ChatState } }
>(
  'chat/openDocumentById',
  async ({ documentVersionId, documentId, documentTitle, audienceFilter, publishedOnly, page, slug }, thunkAPI) => {
    thunkAPI.dispatch(clearMessages());
    let documentVersion: DocumentVersionType | undefined;
    if (documentVersionId) {
      documentVersion = await thunkAPI.dispatch(fetchDocumentVersionThunk({ documentVersionId })).unwrap();
    } else if (documentId) {
      documentVersion = await thunkAPI.dispatch(fetchDocumentVersionThunk({ documentId, publishedOnly })).unwrap();
    } else if (slug) {
      documentVersion = await thunkAPI
        .dispatch(fetchDocumentVersionThunk({ documentId, slug, publishedOnly }))
        .unwrap();
    }
    return documentVersion;
  }
);

/** Return type of the /libraries/${libraryId}/values/document endpoint
 *
 * https://pearl-project.atlassian.net/wiki/spaces/MedPearl/pages/214269953/Pearl+API+Specifications#Library
 */
type PearlApiOrganizationType = {
  topic_subtopic_pairs: { topic: string; subtopic: string }[];
  personas: { id: string; name: string }[];
  careCategories: { id: string; name: string }[];
  patientGroups: { id: string; name: string }[];
  governanceModels: { id: string; name: string }[];
  docCategories: { id: string; name: string }[];
  specialties: string[]; // tags and categories.  e.g. ["Primary Care", "Pediatrics" ]
};
function convertPearlApiOrganizationToOrganizationType(
  org: PearlApiOrganizationType,
  users: [{ id: string; name: string; title: string }],
  orgId: string
): OrganizationType {
  return {
    id: orgId,
    users: listToIdDict(users),
    topics: org.topic_subtopic_pairs,
    personas: org.personas,
    careCategories: org.careCategories,
    patientGroups: org.patientGroups,
    governanceModels: org.governanceModels,
    docCategories: org.docCategories,
    specialties: org.docCategories.map((cat) => cat.name), // We use docCategories as specialties
  };
}

export const fetchOrganizationThunk = createAsyncThunk<OrganizationType, string, { state: RootState }>(
  'chat/fetchOrganization',
  async (orgId: string, thunkAPI) => {
    if (!orgId) {
      throw new Error('Internal error: orgId is required');
    }
    try {
      // With multiple libraries into the picture, and Providence being the default library we'd be storing all the new documents in the default library.
      const libraryId = thunkAPI.getState().user.pearlUserInfo?.defaultLibraryId;
      const [values_reply, users_reply] = await Promise.all([
        pearlApi.get(`/libraries/${libraryId}/values/document`),
        pearlApi.get(`/organizations/${orgId}/users`), // Defaults to without consumers
      ]);
      await thunkAPI.dispatch(fetchPredefinedWidgetsThunk());
      // See https://pearl-project.atlassian.net/wiki/spaces/PearlArchitecture/pages/9371649/Pearl+API+Design
      // For the return types.  Set the typescript types here just to try to make it easier to catch mistakes
      const users: [{ id: string; name: string; title: string }] = users_reply.data.data.users;
      const values: PearlApiOrganizationType = values_reply.data.data;
      return convertPearlApiOrganizationToOrganizationType(values, users, orgId);
    } catch (error) {
      snackNotifications.error(`Failed to fetch information about organization: ${(error as Error).message}`);
      throw error;
    }
  },
  {
    // Check if we already have the organization, and that we aren't already loading it
    condition: (orgId, { getState }) => {
      const state = getState().chat;
      return !state.organizations[orgId] && !state.organizationsLoading[orgId];
    },
  }
);

export async function fetchDocumentVersionOrThrow(
  text: string,
  slug: string | undefined,
  audience?: AudienceType,
  publishedOnly?: boolean,
  searchCategories?: boolean,
  exactMatchOnly?: boolean
): Promise<DocumentVersionType> {
  const { versions: res } = await fetchDocumentVersionsSearch(
    text,
    slug,
    audience || {
      persona: 'Provider', // This is a to work around a bug where it returned results from the wrong organization without this
      careCategory: null,
      patientGroup: null,
    },
    publishedOnly || false,
    searchCategories || false,
    exactMatchOnly || false
  );
  if (res.length === 0) {
    throw new Error(`Document not found: ${text}`);
  } else if (res.length > 1) {
    throw new Error(`Multiple documents found for: ${text}`);
  }
  return res[0] as DocumentVersionType;
}

/** Search DOCUMENT and TRIAGE (note, not EMBEDDABLES) with a title, tag, or searchCategory that contains
 *  (or has exact match) the given text.
 */
export async function fetchDocumentVersionsSearch(
  text: string | undefined,
  slug: string | undefined,
  audience?: AudienceType,
  publishedOnly?: boolean,
  searchCategories?: boolean,
  exactMatchOnly?: boolean,
  limit?: number
) {
  const audienceStr = `${audienceToUrlParam(audience, publishedOnly)}&`;
  if (NO_SERVER) {
    const doc = (
      await pearlApi.get(
        `documentVersions/${publishedOnly ? 'published' : 'draft'}/${encodeURIComponent((text || slug)!.trim())}.json`
      )
    )?.data as DocumentVersionType | undefined;
    if (!doc) {
      throw new Error(`Document not found: ${text}`);
    }
    fixupDocumentVersion(doc);
    documentVersionUpdateGeneratedFields(doc);
    return { versions: [doc], totalVersions: 1 };
  }

  if (slug) {
    // First try fetching by slug
    const version = await fetchDocumentVersionBySlug(slug, publishedOnly ?? false); // already does the convertPearlApiDocumentVersionToDocumentVersionType
    if (version) {
      return { versions: [version], totalVersions: 1 };
    }
  }
  const res = await pearlApi.get(
    `/documentVersions/search?${audienceStr}search=${encodeURIComponent(text!.trim())}&searchCategories=${
      searchCategories ? 'true' : 'false'
    }&titleContains=${exactMatchOnly ? 'false' : 'true'}${limit ? `&limit=${limit}` : ''}`
  );

  const data = res?.data?.data?.versions as
    | (PearlApiDocumentVersionType | PearlApiDocumentVersionSearchType)[]
    | undefined;
  const versions: (DocumentVersionType | DocumentVersionSearchType)[] = [];
  for (const d of data || []) {
    versions.push(await convertPearlApiDocumentVersionToDocumentVersionType(d));
  }
  if (!versions) {
    throw new Error('Failed to fetch document versions');
  }
  return {
    versions: versions as SearchDocumentVersionType,
    totalVersions: res?.data?.data?.total_versions as number,
    queryMeds: res?.data?.data?.metadata?.query_meds as string[],
  };
}

/** Not exported - use dispatch(unsavedUpdatePage(changes))  instead to call from outside this function */
function updateUnsavedChanges(state: ChatState, changes: UnsavedPageChangeType) {
  const { id, document_version_id } = changes;
  const pageId = id || ''; // undefined means it's a new page without an id yet
  const previousChanges = state._unsavedPageChanges[pageId];
  if (pageId === 'LANDING') {
    // This isn't a real page - but a "page" that is used to show the guide pages inside.
    // So there's an internal error if we try to update it.
    throw new Error('Internal error: Trying to update the LANDING page in unsavedUpdatePageFhir');
  }
  if (previousChanges) {
    changes = {
      ...previousChanges,
      ...changes,
    } as UnsavedPageChangeType;
  }
  state._unsavedPageChanges[pageId] = changes;

  // And update the cached unsavedPages
  if (!document_version_id) {
    throw new Error('Internal error: document_version_id is required');
  }
  const documentVersion = state.documentVersions[document_version_id];
  if (!documentVersion) {
    throw new Error(`Internal error: documentVersion not found for ${document_version_id}`);
  }
  if (pageId) {
    const previousPage = documentVersion.pages[pageId];
    if (!previousPage) {
      throw new Error(`Internal error: page not found for ${pageId}`);
    }
    state.unsavedPages[pageId] = {
      ...previousPage,
      ...changes,
    } as PageType;
  } else {
    state.unsavedPages[pageId] = {
      ...defaultNewPage(false, document_version_id),
      ...changes,
    } as PageType;
  }
}

export type TextInputThunkArgs = {
  // Parameters type
  text?: string /* Document name, e.g. "Some Doc" */;
  slug?: string /* Document slug, e.g. "some_doc" */;
  documentId?: string;
  documentVersionId?: string;
  page?: string;
  audience: AudienceType;
  publishedOnly: boolean;
  searchCategories: boolean;
  keep?: boolean;
  howActivated: HowActivatedType; // Just for logging purposes.
};

export const textInputThunk = createAsyncThunk<
  | {
      // Return Type
      versions: SearchDocumentVersionType;
      audience: AudienceType;
      publishedOnly: boolean;
      text?: string;
      specificPage?: string;
      hideSent?: boolean;
      howActivated: HowActivatedType; // Just for logging purposes.
      wasPartialMatch: boolean;
      searchId: string;
      patientMrnHash?: number;
      sessionId?: string;
      searchMetaData?: SearchMetadata;
      search_source?: string;
      totalVersions: number;
      queryMeds?: string[];
    }
  | undefined,
  TextInputThunkArgs, // Parameters type
  { state: { chat: ChatState; fhir: FhirState; user: UserState } }
>(
  '/chat/textInput',
  async ({ text, slug, audience, publishedOnly, searchCategories, keep, howActivated }, thunkAPI) => {
    if (text) {
      if (thunkAPI.getState().chat.isSearching > 1) {
        console.log('Ignoring search because already searching');
        return; // Ignore if already being called.
      }
      if (!keep) {
        thunkAPI.dispatch(clearMessages());
      }
      const isCreatorMode = thunkAPI.getState().chat.creatorMode;

      // A link like:  Foo # bar     means "Foo" but then jump to page bar
      const [base, specificPage] = text.split('#');
      // Note this might be just one document version, containing the full document, or a list of just a bit of info for each document
      if (base) {
        let { versions, totalVersions, queryMeds } = await fetchDocumentVersionsSearch(
          base,
          slug,
          audience,
          publishedOnly,
          searchCategories,
          true // Exact match only
        );
        if (!versions) throw new Error('Failed to fetch document versions');

        // Whether we matched the exact title, or just part of the title.  This is just to keep track of whether to tell the
        // user it was an 'exact' match or not.
        let wasPartialMatch = false;

        const searchId = createRandomId();

        if (versions.length === 1) {
          // This happens when there's not an exact match, but there is one partial match under the current audience.  In that case, fetch that partial match
          // if we weren't already given the full document
          const version: DocumentVersionType | undefined = isDocumentVersionType(versions[0]!)
            ? versions[0]!
            : await fetchDocumentVersionById(versions[0]!.id);
          versions = version ? [version] : [];
          wasPartialMatch = true;
        }
        const patientMrnHash = hashCode(thunkAPI.getState().fhir.patientContext?.mrn);
        const sessionId = thunkAPI.getState().user.pearlUserInfo?.sessionId;
        // When keep is true, we don't remove old messages.  So user clicked an external link.
        // So set hideSent to true as well so we don't have two bubbles with the name of the link
        const search_source = selectSearchSourceType(thunkAPI.getState() as RootState);
        return {
          versions,
          audience,
          publishedOnly,
          text,
          specificPage,
          hideSent: keep,
          howActivated,
          wasPartialMatch,
          searchId,
          patientMrnHash,
          sessionId,
          search_source,
          totalVersions,
          queryMeds,
        };
      }
    }
    return;
  },
  {
    condition: ({ text, howActivated, keep }, { getState }) => {
      if (howActivated === 'algorithmClick' && getState().chat.messages.length === 0) {
        return false;
      }
      return true;
    },
  }
);

// BOOKMARK IS FAVORITE FOR USERS
export const bookmarkCurrentDocumentThunk = createAsyncThunk<
  void,
  { documentId: string; bookmark: boolean },
  { state: RootState }
>('chat/bookmarkCurrentDocumentThunk', async ({ documentId, bookmark }, thunkAPI) => {
  try {
    if (bookmark) {
      await pearlApi.post(`/bookmarks/document/${documentId}`);
    } else {
      await pearlApi.delete(`/bookmarks/document/${documentId}`);
    }
  } catch (err) {
    snackNotifications.error(`Failed to ${bookmark ? 'add' : 'remove'} bookmark: ${(err as Error).message}`);
    throw err;
  }
});

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // Redux Toolkit allows us to write "mutating" logic in reducers. It
    // doesn't actually mutate the state because it uses the Immer library,
    // which detects changes to a "draft state" and produces a brand new
    // immutable state based off those changes
    setCreatorMode: (state, action: PayloadAction<boolean>) => {
      state.creatorMode = action.payload;
      state.publishedOnly = !state.creatorMode; // default to publishedOnly when not in creator mode
    },
    setPublishedOnly: (state, action: PayloadAction<boolean>) => {
      state.publishedOnly = action.payload;
    },
    setDocumentToSearchFor: (state, action: PayloadAction<TextInputThunkArgs | undefined>) => {
      // Changes to this will cause textInputThunk (if .text is set) or openDocumentByIdThunk otherwise to be called
      state.currentSearch = action.payload;
    },
    clearWelcome: (state) => {
      if (state.messages[0] === WELCOME_MESSAGE) {
        state.messages.shift();
      }
    },
    clearMessages: (state) => {
      rollbackToMessage(state, -1); // Clear the screen and unset the selected values
    },
    clearMessagesAndAddWelcome: (state) => {
      rollbackToMessage(state, -1); // Clear the screen and unset the selected values
      // Clear any search hash
      window.location.hash = '';
      addWelcome();
      state.currentSearch = undefined;
    },
    addWelcome: (state) => {
      // add welcome message only if chat is empty and only if app is creator
      if (state.messages.length === 0 && state.creatorMode && !state.isSearching) {
        state.messages.push(WELCOME_MESSAGE);
      }
    },
    returnToHereClicked: (state, action: PayloadAction<number>) => {
      const messageIndex = state.returnToHere[action.payload]?.messageIndex;
      if (messageIndex == null) {
        return;
      }
      rollbackAndRunPage({ messageIndex, clearSelected: true }, makeRunAlgorithmCallbacks(state));
    },
    clearMessagesAndSetToPage: (state, action: PayloadAction<{ page?: PageType; title: string }>) => {
      const { page, title } = action.payload;
      rollbackToMessage(state, -1); // Clear the screen and unset the selected values
      /* When calling another reducer, you have to set the type to the name of the function */
      if (!page) {
        return;
      }
      state.messages.push({
        is_sent: true,
        index: state.messages.length,
        extra: { type: 'START_PAGE', document_name: title, page_name: page.isRoot ? '' : page.name },
      });

      /** This call will push new messages to the passed in state.messages and update state.selected */
      addPageToMessages({ page }, makeRunAlgorithmCallbacks(state));
    },
    setMessageFlipped: (
      state,
      action: PayloadAction<{
        messageIndex?: number | undefined;
        page?: PageType; // This is the actual page being edited. e.g. if we have a GUIDE inside of a TABS, this will be the GUIDE. If we are creating a new page, this is the new page.
        swimlaneIndex?: number;
      }>
    ) => {
      const { messageIndex, page, swimlaneIndex } = action.payload;
      state.messageFlipped =
        messageIndex != null && messageIndex >= 0
          ? {
              index: Math.floor(messageIndex),
              page,
              swimlaneIndex,
            }
          : undefined;
      if (!state.messageFlipped) {
        chatSlice.caseReducers.cancelUnsavedChangesAndUnflip(state);
      }
      if (page && !page.id) {
        // Save the new page as an unsaved page
        updateUnsavedChanges(state, page);
      }
    },
    setPage: (state, action: PayloadAction<PageType>) => {
      const page = action.payload;
      const documentVersion = state.documentVersions[page.document_version_id];
      if (!documentVersion) throw new Error('Internal error: setPage with no documentVersion');
      const pages = documentVersion.pages;
      pages[page.id] = page;
      if (page.isRoot && documentVersion && documentVersion.algorithmRootPageId !== page.id) {
        // We have set a page which is root, so unset root on the previous root page
        const oldRoot = documentVersion.pages[documentVersion.algorithmRootPageId!];
        if (oldRoot) {
          oldRoot.isRoot = false;
        }
        documentVersion.algorithmRootPageId = page.id;
      }
      documentVersionUpdateGeneratedFields(documentVersion);
      state.messages.forEach((message) => {
        if (message.pages) {
          for (let i = 0; i < message.pages.length; i++) {
            if (message.pages[i]!.id === page.id) {
              message.pages[i] = page;
            }
          }
        }
      });
      if (state.messageFlipped && state.messageFlipped.page?.id === page.id) state.messageFlipped.page = page;
    },
    /** The SAVED page has changed. e.g. we just did a network save, or the server notified us that the page changed.
     *  This will not save the page to the server, but just update the state.
     *  @see unsavedUpdatePage
     */
    updatePage: (state, action: PayloadAction<Partial<PageType> & { document_version_id: string; id: string }>) => {
      const pageId = action.payload.id;
      if (pageId === 'LANDING') {
        // This isn't a real page - but a "page" that is used to show the guide pages inside.
        // So there's an internal error if we try to update it.
        throw new Error('Internal error: Trying to update the LANDING page in updatePage');
      }
      const page = state.documentVersions[action.payload.document_version_id]?.pages[pageId];
      if (!page) {
        // Create new page
        chatSlice.caseReducers.setPage(state, {
          type: 'setPage',
          payload: action.payload as PageType,
        });
        const newPageMessage = state.messages.find((message) => message.pages?.[0] && !message.pages?.[0]?.id);
        if (newPageMessage) {
          const newPage = action.payload as PageType;
          newPageMessage.pages = [newPage];
          newPageMessage.content = undefined;
          const pagesUsed = (findLast(state.messages, (message) => message.pagesUsed) as MessageType)?.pagesUsed || [];
          newPageMessage.pagesUsed = [...pagesUsed, newPage.id];
        }
      } else {
        Object.assign(page, action.payload);
        chatSlice.caseReducers.setPage(state, {
          type: 'setPage',
          payload: page,
        });
      }
      /* We might need to update the unsaved page as well.
       *  For example, we allow updating the page.meta field (e.g. page markings), without locking etc a page.
       */
      if (state._unsavedPageChanges[pageId]) {
        updateUnsavedChanges(state, action.payload);
      }
    },
    /** Same as updatePage, but takes an array of changes */
    updatePages: (
      state,
      action: PayloadAction<(Partial<PageType> & { document_version_id: string; id: string })[]>
    ) => {
      action.payload.forEach((payload) => {
        chatSlice.caseReducers.updatePage(state, {
          type: 'updatePage',
          payload,
        });
      });
    },
    /** Record temporary changes made to the page.  When the user presses save, these will be sent to the server.
     *  Or the user might cancel this.
     *  To prevent potential problems, don't allow updating fhir here.  Use unsavedUpdatePageFhir instead.
     *  To actually save this after this call, @see saveUnsavedChangesThunk() */
    unsavedUpdatePage: (state, action: PayloadAction<UnsavedPageChangeWithoutFhirType>) => {
      const pageId = action.payload.id;
      if (pageId === 'LANDING') {
        // This isn't a real page - but a "page" that is used to show the guide pages inside.
        // So there's an internal error if we try to update it.
        throw new Error('Internal error: Trying to update the LANDING page in unsavedUpdatePage');
      }
      // Make sure there's no fhir changes
      if (action.payload.fhir) {
        throw new Error(
          'Internal error: Trying to update fhir in unsavedUpdatePage. Use unsavedUpdatePageFhir instead'
        );
      }
      updateUnsavedChanges(state, action.payload);
    },
    /** Record temporary changes made to the page.  When the user presses save, these will be sent to the server.
     *  Or the user might cancel this.
     *
     *  Changes to fhir will be set (i.e. not merged). Intention for use is an admin editting the json manually.
     *
     *  To actually save this after this call, @see saveUnsavedChangesThunk() */
    unsavedUpdatePageRaw: (state, action: PayloadAction<UnsavedPageChangeType>) => {
      const pageId = action.payload.id;
      if (pageId === 'LANDING') {
        // This isn't a real page - but a "page" that is used to show the guide pages inside.
        // So there's an internal error if we try to update it.
        throw new Error('Internal error: Trying to update the LANDING page in unsavedUpdatePage');
      }
      updateUnsavedChanges(state, action.payload);
    },
    /* Very similiar to unsavedUpdatePage in that it will update the fhir on the page,
       but it will merge the fhir, rather than overwrite it */
    unsavedUpdatePageFhir: (
      state,
      action: PayloadAction<{ id: string; document_version_id: string; idsToFhir: PageFhirType['idsToFhir'] }>
    ) => {
      const currentFhir =
        state.unsavedPages[action.payload.id || '']?.fhir ||
        state.documentVersions[action.payload.document_version_id]?.pages[action.payload.id || '']?.fhir;

      updateUnsavedChanges(state, {
        id: action.payload.id,
        document_version_id: action.payload.document_version_id,
        fhir: {
          ...currentFhir,
          idsToFhir: {
            ...currentFhir?.idsToFhir,
            ...action.payload?.idsToFhir,
          },
        },
      });
    },
    /* Very similiar to unsavedUpdatePage in that it will update the fhir on the page,
       but it will merge the fhir, rather than overwrite it */
    /**
     * When we are in the algorithm view and click Create a new page, there is a two step process.
     * First we add the new page (with no id) to the messages.  Then we call unsavedSetPageIfNotSet()
     * to actually flip the message.
     *
     * This call will flip the message to edit it, if no other messages are being editted currently
     */
    unsavedSetPageIfNotSet: (state, action: PayloadAction<MessageType>) => {
      const message = action.payload;

      if (message.pages?.[0] && !state.messageFlipped && !state.unsavedPages[message.pages?.[0].id]) {
        // Do a sanity check that this page is actually in our list of pages.  It can happen in some race
        // situations that it's not.
        const isPageInMessages = state.messages.some((m) => m.pages?.[0] && m.pages?.[0].id === message.pages?.[0]!.id);
        if (!isPageInMessages) return;

        updateUnsavedChanges(state, message.pages?.[0]);
        state.messageFlipped =
          message.index === -1
            ? undefined
            : {
                index: message.index,
                page: undefined, // This message only has one page, so no need to disambiguate
                swimlaneIndex: undefined,
              };
      }
    },
    cancelUnsavedChangesAndUnflip: (state) => {
      state._unsavedPageChanges = {};
      state.unsavedPages = {}; // Delete the cached combined unsavedPages
      state.messageFlipped = undefined;
      // A page created - lets just remove it.
    },
    createUnsavedPageAndAddToMessages: (state, action: PayloadAction<{ page: PageType }>) => {
      const { page } = action.payload;
      const documentVersion = state.documentVersions[page.document_version_id];

      if (page.isRoot) {
        rollbackToMessage(state, -1);
        const title = documentVersion?.title;
        state.messages = [
          {
            is_sent: true,
            index: 0,
            extra: { type: 'START_PAGE', document_name: title! },
          },
        ];
      }
      const pagesUsed = (findLast(state.messages, (message) => message.pagesUsed) as MessageType)?.pagesUsed || [];
      if (!documentVersion) {
        throw new Error('Trying to create new page with invalid document_version_id');
      }
      const index = state.messages.length;
      state.messages.push({
        is_sent: false,
        pages: [page],
        pagesUsed,
        selected: { ...state.selected },
        index,
      });
      if (page.id !== '') {
        throw new Error('Internal error: page.id should be empty for new pages');
      }
      state._unsavedPageChanges[page.id] = page;
      state.unsavedPages[page.id] = page;
      state.messageFlipped = { index }; // Message has only one page, so no need to disambiguate by setting page
    },
    setPageTooltip: (
      state,
      action: PayloadAction<{ page_id: string; document_version_id: string; new_content: string }>
    ) => {
      const page = selectPageByIdLocalized(state, action.payload.document_version_id, action.payload.page_id);
      const isEmpty = isQuillContentEmpty(action.payload.new_content);
      if (page) {
        page.tooltip = isEmpty ? undefined : action.payload.new_content;
        state.messages.forEach((message) => {
          if (message.pages) {
            for (let i = 0; i < message.pages.length; i++) {
              if (message.pages[i]!.id === page.id) {
                message.pages[i] = page;
              }
            }
          }
        });
      }
    },
    toggleOptionKey: (
      state,
      action: PayloadAction<{
        page: PageType;
        messageIndex: number;
        key: string;
        value: string;
      }>
    ) => {
      // Do not allow changing the options while we are editing something
      if (state.messageFlipped) {
        snackNotifications.warning('Currently editing a page - please save or cancel before changing options');
        return;
      }
      toggleOptionKey_(action.payload, makeRunAlgorithmCallbacks(state));
      // Suggested place to log the current state to the backend asynchronously
      // logToBackendUserIsHere(state.messages, state.selected);
    },
    confirmMessageNext: (state, action: PayloadAction<{ messageIndex: number }>) => {
      // Confirm to move on to the next message. Parameter should be the same as message.length - 1, and is provided
      // just to avoid possible timing issues.  It will rollback to this message just to be safe.
      // It sets message.extra.have_confirmed = true
      // and then reruns the page, displaying the next message
      confirmMessageNext_(action.payload.messageIndex, makeRunAlgorithmCallbacks(state));
    },
    updateDocumentVersion: (state, action: PayloadAction<{ id: string } & Partial<DocumentVersionType>>) => {
      if (!state.documentVersions[action.payload.id])
        throw new Error("Internal Error - updateDocumentVersion for id that doesn't exist");
      const newDocumentVersion = { ...state.documentVersions[action.payload.id]!, ...action.payload };
      state.documentVersions[action.payload.id] = newDocumentVersion;
      if (isPublishedVersion(newDocumentVersion)) {
        state.publishedDocuments[newDocumentVersion.document.id] = newDocumentVersion;
      } else if (isPrePublished(newDocumentVersion.state)) {
        state.prepublishedDocuments[newDocumentVersion.document.id] = newDocumentVersion;
      }
    },
    setDocumentVersion: (state, action: PayloadAction<{ documentVersion: DocumentVersionType }>) => {
      // Creates the document at the same time.
      const documentVersion = action.payload.documentVersion;
      state.documentVersions[documentVersion.id] = documentVersion;
      if (isPublishedVersion(documentVersion)) {
        state.publishedDocuments[documentVersion.document.id] = documentVersion;
      } else if (isPrePublished(documentVersion.state)) {
        state.prepublishedDocuments[documentVersion.document.id] = documentVersion;
      }
    },
    clearDocumentVersionCache: (state, action: PayloadAction<{ documentVersionId: string; documentId: string }>) => {
      delete state.documentVersions[action.payload.documentVersionId];
      delete state.documentVersionsLoading[action.payload.documentVersionId];
      delete state.documentVersionLocks[action.payload.documentVersionId];
      delete state.publishedDocuments[action.payload.documentId];
      delete state.prepublishedDocuments[action.payload.documentId];
    },
    updateDocumentVersionTopic: (
      state,
      action: PayloadAction<{ id: string; topic_index: number; topic?: string; subtopic?: string }>
    ) => {
      const { id, topic_index, topic, subtopic } = action.payload;
      const documentVersion = state.documentVersions[id];
      if (!documentVersion)
        throw new Error('Internal error: updateDocumentVersionTopic documentVersion does not exist');
      const topics = documentVersion.topics;
      while (topics.length <= topic_index) topics.push({ topic: '', subtopic: '' });
      const topic_orig = topics[topic_index];
      if (!topic_orig) throw new Error('Internal error: updateDocumentVersionTopic topic does not exist');
      topics[topic_index] = {
        topic: topic ?? topic_orig.topic,
        subtopic: subtopic ?? topic_orig.subtopic,
      };
      documentVersion.topics = topics.filter((topic) => topic.topic !== '' || topic.subtopic !== '');
    },
    tabPageSelected: (
      state,
      action: PayloadAction<{
        documentVersionId: string;
        tabPageId: string;
        link: string | undefined;
        tabValue: string;
      }>
    ) => {
      const { documentVersionId, tabPageId, link, tabValue } = action.payload;
      if (link) {
        state.tabPageSelected = { documentVersionId, tabPageId, link, tabValue };
      } else {
        state.tabPageSelected = undefined;
      }
    },
    setSearchQueryMetaData: (state, action: PayloadAction<SearchMetadata>) => {
      state.searchMetadata = action.payload;
    },
    resetSearchQueryMetaData: (state) => {
      state.searchMetadata = undefined;
    },
  },
  extraReducers: (builder) => {
    addCreatorExtraReducers(builder)
      .addCase(fetchOrganizationThunk.pending, (state, data) => {
        const organizationId = data.meta.arg;
        state.organizationsLoading[organizationId] = true;
      })
      .addCase(fetchOrganizationThunk.fulfilled, (state, data) => {
        const organization: OrganizationType = data.payload;
        state.organizations[organization.id] = organization;
        delete state.organizationsLoading[organization.id];
      })
      .addCase(fetchOrganizationThunk.rejected, (state, data) => {
        const organizationId = data.meta.arg;
        delete state.organizationsLoading[organizationId];
      })
      .addCase(fetchDocumentVersionThunk.pending, (state, data) => {
        // Check if already loaded
        const documentVersionId = data.meta.arg.documentVersionId;
        const shouldSetLoadingState = documentVersionId && !state.documentVersions[documentVersionId];
        if (shouldSetLoadingState) {
          state.documentVersionsLoading[documentVersionId] = {
            isLoading: true,
            notFound: false,
            lastChecked: performance.now(),
          };
        }
      })
      .addCase(fetchDocumentVersionThunk.rejected, (state, data) => {
        const documentVersionId = data.meta.arg.documentVersionId;
        if (documentVersionId) {
          state.documentVersionsLoading[documentVersionId] = {
            isLoading: false,
            notFound: true,
            error: data.error.message,
            lastChecked: performance.now(),
          };
        }
      })
      .addCase(fetchDocumentVersionThunk.fulfilled, (state, data) => {
        const documentVersion: DocumentVersionType | undefined = data.payload;
        if (data.meta.arg.documentVersionId) {
          state.documentVersionsLoading[data.meta.arg.documentVersionId] = {
            isLoading: false,
            notFound: !documentVersion,
            lastChecked: performance.now(),
          };
        }
        if (documentVersion) {
          // See if it actually changed.  Prevent unneccessary updates to the UI
          // TODO: Make this smarter?  Work with CKEditor?
          if (
            state.documentVersions[documentVersion.id] &&
            JSON.stringify(state.documentVersions[documentVersion.id]) === JSON.stringify(documentVersion)
          ) {
            console.log('No new changes in fetched document');
          } else {
            if (state.documentVersions[documentVersion.id]) {
              console.log('Document has changed!  Updating - this might cause weird glitches!');
              // console.log('Before:', JSON.stringify(state.documentVersions[documentVersion.id]));
              // console.log('After:', JSON.stringify(documentVersion));
            }
            state.documentVersions[documentVersion.id] = documentVersion;
            if (isPublishedVersion(documentVersion)) {
              state.publishedDocuments[documentVersion.document.id] = documentVersion;
            } else if (isPrePublished(documentVersion.state)) {
              state.prepublishedDocuments[documentVersion.document.id] = documentVersion;
            }
          }
        } else {
          // Need to still set it to undefined, so we know it's not found
          const documentId = data.meta.arg.documentId;
          if (documentId) {
            state.publishedDocuments[documentId] = null;
            state.prepublishedDocuments[documentId] = null;
          }
        }
      })
      .addCase(textInputThunk.pending, (state) => {
        state.isSearching += 1;
      })
      .addCase(textInputThunk.rejected, (state) => {
        state.isSearching -= 1;
        state.searchMetadata = undefined;
        addWelcome();
        // This can happen when a previous textInput is cancelled.
        // happens when loading, for example.  But we might have cleared the messages.
      })
      .addCase(textInputThunk.fulfilled, (state, data) => {
        state.isSearching -= 1;
        if (!data.payload) return;
        const {
          versions,
          publishedOnly,
          text,
          specificPage,
          hideSent,
          wasPartialMatch,
          searchId,
          howActivated,
          totalVersions,
          queryMeds,
        } = data.payload;
        if (!text) return;
        if (!hideSent) {
          state.messages.push({
            is_sent: true,
            index: state.messages.length,
            extra: { type: 'START_PAGE', document_name: text },
          });
        }
        if (versions.length > 1 || (versions.length === 1 && !isDocumentVersionType(versions[0]!))) {
          state.queryMeds = queryMeds;
          state.messages.push({
            is_sent: false,
            index: state.messages.length,
            extra: {
              type: 'MULTIPLE_MATCHES',
              publishedOnly,
              versions: versions as DocumentVersionSearchType[],
              searchTerm: text,
              searchId,
              howActivated,
              totalVersions,
            },
          });
        } else if (versions.length === 0) {
          state.messages.push({
            is_sent: false,
            index: state.messages.length,
            extra: {
              type: 'NO_SEARCH_MATCH',
              publishedOnly,
              howActivated,
              searchTerm: text,
              searchId,
            },
          });
        } else {
          if (versions.length === 1 && isDocumentVersionType(versions[0]!)) {
            state.documentVersions[versions[0].id] = versions[0];
            state.messages.push({
              is_sent: false,
              no_bubble: true,
              index: state.messages.length,
              extra: {
                // Shows: One exact match found
                was_partial_match: wasPartialMatch,
                type: 'MATCH_FOUND',
                document_version_id: versions[0]?.id,
                document_audiences: versions[0].audiences,
              },
            });
          }
          /* Show the first guide/algorithm in the actual document */
          addChatMessage(
            {
              hide_sent: true,
              message: {
                humanTyped: true,
                text,
                document_version_id: versions[0]?.id,
              },
              specificPage,
              howActivated: 'algorithmClick',
            },
            makeRunAlgorithmCallbacks(state)
          );
        }
      })
      .addCase(openDocumentByIdThunk.pending, (state, data) => {
        state.isSearching += 1;
      })
      .addCase(openDocumentByIdThunk.rejected, (state, data) => {
        state.isSearching -= 1;
        state.messages.push({
          is_sent: false,
          no_bubble: true,
          content: '<i>Network error: Failed to load document</i>',
          index: state.messages.length,
        });
        return;
      })
      .addCase(openDocumentByIdThunk.fulfilled, (state, data) => {
        state.isSearching -= 1;
        const documentVersion = data.payload;
        const { publishedOnly, documentTitle, page, slug } = data.meta.arg;
        rollbackToMessage(state, -1);
        if (!documentVersion) {
          state.messages.push(
            {
              is_sent: true,
              index: 0,
              extra: { type: 'START_PAGE', document_name: documentTitle || '' },
            },
            {
              is_sent: false,
              no_bubble: true,
              content: `<i>'${documentTitle || slug}' does not have a ${
                publishedOnly ? 'published' : 'viewable'
              } version or you do not have permissions to this document</i>`,
              index: 1,
            }
          );
          return;
        }
        if (publishedOnly && documentVersion.state !== DocumentVersionState.PUBLISHED) {
          state.messages.push({
            is_sent: false,
            no_bubble: true,
            content: '<i>Document is not in published state, but "Published Only" is selected</i>',
            index: state.messages.length,
          });
          return;
        }
        const text = documentVersion.title + (page ? ` #${page}` : '');
        // We have a documentversion that we have just loaded and want to show
        state.messages.push({
          is_sent: true,
          index: state.messages.length,
          extra: { type: 'START_PAGE', document_name: documentVersion.title, page_name: page },
        });
        // This next message doesn't really make sense, but is needed if we don't want the voting
        // to overlap.
        // Note there's another place we show 'One exact match found' - see textInputThunk.fulfilled
        state.messages.push({
          is_sent: false,
          no_bubble: true,
          index: state.messages.length,
          extra: {
            // Shows: One exact match found (Note this is when loading a document by id)
            was_partial_match: false,
            type: 'MATCH_FOUND',
            document_version_id: documentVersion.id,
            document_audiences: documentVersion.audiences,
          },
        });
        addChatMessage(
          {
            hide_sent: true,
            message: { humanTyped: true, text, document_version_id: documentVersion.id },
            howActivated: 'urlLink',
            specificPage: page,
          },
          makeRunAlgorithmCallbacks(state)
        );
      })
      .addCase(bookmarkCurrentDocumentThunk.pending, (state, data) => {
        const { documentId, bookmark } = data.meta.arg;
        Object.values(state.documentVersions).forEach((documentVersion) => {
          if (documentVersion!.document.id === documentId) {
            documentVersion.bookmarkedByYou = bookmark;
          }
        });
      })
      .addCase(bookmarkCurrentDocumentThunk.rejected, (state, data) => {
        const { documentId, bookmark } = data.meta.arg;
        Object.values(state.documentVersions).forEach((documentVersion) => {
          if (documentVersion!.document.id === documentId) {
            documentVersion.bookmarkedByYou = !bookmark;
          }
        });
      })
      .addCase(fetchDocumentForExternalReviewThunk.fulfilled, (state, data) => {
        state.isSearching = -1;
        // Similar to openDocumentByIdThunk.fulfilled- but has no restrictions to publishedOnly and audience.
        rollbackToMessage(state, -1);

        const { documentVersion, consultant_reviewer_info } = data.payload;
        state.documentVersions[documentVersion.id] = documentVersion;

        state.messages.push({
          is_sent: true,
          index: state.messages.length,
          extra: { type: 'START_PAGE', document_name: documentVersion.title },
        });
        state.messages.push({
          is_sent: false,
          no_bubble: true,
          index: state.messages.length,
          extra: {
            // Shows: One exact match found (Note this is when loading a document by id)
            was_partial_match: false,
            type: 'MATCH_FOUND',
            document_version_id: documentVersion.id,
            document_audiences: documentVersion.audiences,
          },
        });
        addChatMessage(
          {
            hide_sent: true,
            message: { humanTyped: true, text: documentVersion.title, document_version_id: documentVersion.id },
            howActivated: 'urlLink',
            specificPage: '',
          },
          makeRunAlgorithmCallbacks(state)
        );
      })
      .addCase(fetchDocumentForExternalReviewThunk.rejected, (state, data) => {
        console.error('fetchDocumentForExternalReviewThunk rejected');
      });
  },
});

export const {
  addWelcome,
  cancelUnsavedChangesAndUnflip,
  clearDocumentVersionCache,
  clearMessages,
  clearMessagesAndAddWelcome,
  clearMessagesAndSetToPage,
  clearWelcome,
  confirmMessageNext,
  createUnsavedPageAndAddToMessages,
  resetSearchQueryMetaData,
  returnToHereClicked,
  setCreatorMode,
  setDocumentToSearchFor,
  setDocumentVersion,
  setMessageFlipped,
  setPage,
  setPageTooltip,
  setPublishedOnly,
  setSearchQueryMetaData,
  tabPageSelected,
  toggleOptionKey,
  unsavedSetPageIfNotSet,
  unsavedUpdatePage,
  unsavedUpdatePageFhir,
  unsavedUpdatePageRaw,
  updateDocumentVersion,
  updateDocumentVersionTopic,
  updatePage,
  updatePages,
} = chatSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectCreatorMode = (state: RootState): boolean => state.chat.creatorMode;
export const selectMessages = (state: RootState): MessageType[] => state.chat.messages;
export const selectQueryMeds = (state: RootState): string[] | undefined => state.chat.queryMeds || undefined;
export const selectMessagesByPageId = createSelector([selectMessages], (messages) =>
  messages.reduce((map, message) => {
    message.pagesUsed?.forEach((page_id) => {
      map[page_id] = message;
    });
    return map;
  }, {} as { [id: string]: MessageType })
);

/** This the list of answers for the all questions that the user has answered.
 *  This is not actually used anywhere, because we never want to know the list of selected
 *  keys for the newest message, only the message we are currently evaluating.
 *  For example, say we have:
 *   -  message 1 is a question
 *   -  message 2 is a swimlane
 *   -  message 3 is a another question
 *
 *  The swimlane in message 2 only cares about your answers in message 1 and before, not what you
 *  selected for messsage 3.
 *  */
export const selectSelectedKeys = (state: RootState) => state.chat.selected;

/** The answers that the user has given up to this message. See comment in @see selectSelectedKeys
 *  Note we floor the messageIndex, because we use a fractional messageIndex when we have multiple
 *  pages in a single message.
 */
export const selectSelectedKeysForMessage = (state: RootState, messageIndex: number) => {
  return state.chat.messages[Math.floor(messageIndex)]?.selected;
};

/** Iterating from bottom of messages to get user selections from all messages
 *  This will be useful to get all user selections when we have more than 1 algorithms loaded in user flow
 *  @note This isn't technically right, since it will override keys between different documents.
 *        However keys have random ids by default and are unlikely to override each other, so it will work 99% of the time.
 *        Would need to make the key something like: ${pages[0]?.document_version_id}_${key} to cover the last 1% of test cases.
 */
export const selectAllUserSelectionsFromAllMessages = createSelector([selectMessages], (messages) =>
  messages.reduceRight((map, message) => {
    if (!!message.selected) {
      Object.keys(message.selected).forEach((key) => {
        map[key] ??= message.selected?.[key]!;
      });
    }
    return map;
  }, {} as { [key: string]: SelectedButtonInfo })
);

export const selectPublishedOnly = (state: RootState): boolean => state.chat.publishedOnly;

// organizations
export const selectOrganizationById = (state: RootState, id: string | undefined): OrganizationType | undefined =>
  state.chat.organizations[id as string];

export const selectCurrentUsersOrganization = (state: RootState): OrganizationType | undefined =>
  state.chat.organizations[state.user.pearlUserInfo?.orgId!];
export const selectCurrentUsersOrganizationUsers = (state: RootState) => selectCurrentUsersOrganization(state)?.users;

/** pages
 *  Note this this won't return any unsaved page changes.  Use selectUnsavedPageById for that.
 */
export const selectPageById = (
  state: RootState,
  document_version_id: string | undefined,
  id: string
): PageType | undefined => state.chat.documentVersions[document_version_id!]?.pages[id];

/** Note this returns the cached combination of _unsavedPageChanges and the actual page.
 * It's intentional to not have a select to return the _unsavedPageChanges directly because it shouldn't ever be needed.
 * Rethink if you think you want access to it */
export const selectUnsavedPageById = (state: RootState, id: string) => state.chat.unsavedPages[id];
/** Return the unsaved page (if there are unsaved changes) or the page loaded from network (if there are not) */
export const selectLatestPageById = (state: RootState, document_version: string, id: string) =>
  state.chat.unsavedPages[id] || state.chat.documentVersions[document_version]?.pages[id];

export const selectPageByDocumentVersionAndTitleWithChanges = (
  state: RootState,
  document_version_id: string | undefined,
  title: string | undefined
) => {
  if (!title) return;
  const documentVersion = selectDocumentVersion(state, document_version_id);

  const page_id = documentVersion?.nameToPageId[title as string];
  return page_id ? selectLatestPageById(state, document_version_id!, page_id) : undefined;
};

// document versions
export const selectDocumentVersion = (
  state: RootState,
  document_version_id: string | undefined
): DocumentVersionType | undefined => state.chat.documentVersions?.[document_version_id!];

export const selectDocumentVersionPages = createSelector(
  [(state: RootState, document_version_id?: string) => selectDocumentVersion(state, document_version_id)?.pages],
  (pages) => Object.values(pages || [])
);

export const selectDocumentVersionPageIdSet = createSelector(
  [selectDocumentVersion],
  (documentVersion) => new Set(documentVersion?.pageIds)
);

export const selectAlgorithmRootId = (state: RootState, document_version_id: string | undefined) =>
  selectDocumentVersion(state, document_version_id)?.algorithmRootPageId;

const selectIdToDocumentVersionDict = (state: RootState) => state.chat.documentVersions;
export const selectDocumentVersionById = (state: RootState, id: string | undefined): DocumentVersionType | undefined =>
  state.chat.documentVersions[id as string];
export const selectMutableDocVersionsListCopy = createSelector([selectIdToDocumentVersionDict], (docversions) =>
  Object.values(docversions)
);

export const selectPossibleKeysForSwimlane = createSelector(
  [
    selectAlgorithmRootId,
    (state, document_version_id) => selectDocumentVersion(state, document_version_id)?.pages,
    (state, document_version_id) => selectDocumentVersion(state, document_version_id)?.nameToPageId,
    (state, document_version_id, page_name: string) => page_name,
  ],
  (algorithmRootPageId, pages, nameToPageId, swimlane_page_name) => {
    // Note, we need the swimlane name instead of id, because if the swimlane is new and hasn't been saved yet, it
    // wont have an id.
    if (!swimlane_page_name || !nameToPageId || !pages) return undefined;
    return findKeys(algorithmRootPageId, undefined, pages, nameToPageId, swimlane_page_name);
  }
);

export const selectPossibleKeysForSwimlaneAsArray = createSelector([selectPossibleKeysForSwimlane], (set) =>
  set ? Array.from(set.data) : []
);

// document versions loading
export const selectDocumentVersionLoading = (
  state: RootState,
  document_version_id: string | undefined
): DocumentVersionLoading | undefined => state.chat.documentVersionsLoading[document_version_id as string];

export const selectIsSaving = (state: RootState): boolean => state.chat.isSaving;

// For now, the org code for a document version is actually just the user's org code
export const selectOrgCodeFromDocumentVersionId = (state: RootState, id: string | undefined) =>
  state.user.pearlUserInfo?.orgCode;
// export const selectOrgCodeFromDocumentVersionId = (state: RootState, id: string | undefined): string => state.chat.documents[state.chat.documentVersions[id as any]?.document_id]?.library?.organization?.orgCode;

// messages
export const selectMessageFlippedIndex = (state: RootState) => state.chat.messageFlipped?.index;
export const selectIsAnyMessageFlipped = (state: RootState) => state.chat.messageFlipped !== undefined;
export const selectIsMessageFlipped = (state: RootState, message: MessageType): boolean =>
  state.chat.messageFlipped ? message.index === state.chat.messageFlipped.index : false;

// document id by version
export const selectDocumentIdByVersion = (
  state: RootState,
  document_version_id: string | undefined
): string | undefined => state.chat.documentVersions[document_version_id!]?.document?.id;

export const selectTabPageSelected = (
  state: RootState,
  document_version_id: string | undefined,
  page_id: string | undefined
) => {
  const tabPageSelected = state.chat.tabPageSelected;
  if (!tabPageSelected || !document_version_id || !page_id) return undefined;
  if (tabPageSelected.documentVersionId !== document_version_id || tabPageSelected.tabPageId !== page_id)
    return undefined;
  return selectPageByDocumentVersionAndTitleWithChanges(state, document_version_id, tabPageSelected.link);
};

export const selectTabValue = (
  state: RootState,
  document_version_id: string | undefined,
  page_id: string | undefined
) => {
  const tabPageSelected = state.chat.tabPageSelected;
  if (!tabPageSelected || !document_version_id || !page_id) return undefined;
  if (tabPageSelected.documentVersionId !== document_version_id || tabPageSelected.tabPageId !== page_id)
    return undefined;
  return tabPageSelected.tabValue;
};

export const selectDocumentLockByVersion = (
  state: RootState,
  document_version_id: string | undefined
): DocumentVersionLockType | undefined => state.chat.documentVersionLocks[document_version_id!];

export default chatSlice.reducer;

// textInputThunk was called
export const selectIsSearching = (state: RootState) => state.chat.isSearching > 0;

/** Note I use named functions here, to make the callback stack more readable when debugging */
export function makeRunAlgorithmCallbacks(state: ChatState): RunAlgorithmCallbacks {
  return {
    addReturnToHere: function addReturnToHereCB(messageIndex: number, info: ReturnToHereInfo) {
      state.returnToHere = state.returnToHere.filter((x) => x.messageIndex !== messageIndex);
      state.returnToHere.push({ messageIndex, info });
    },
    getDocumentVersion: function getDocumentVersionCB(id: string | undefined) {
      return state.documentVersions[id!];
    },
    getDocumentVersionByTag: function getDocumentVersionByTagCB(tag: string) {
      return selectTagToDocumentVersionLocalized(state, tag, state.publishedOnly);
    },
    rollbackToMessage: function rollbackToMessageCB(index: number, pageId?: string, clearSelected?: boolean) {
      return rollbackToMessage(state, index, pageId, clearSelected);
    },
    addToPagesUsed: function addToPagesUsedCB(params: { pageId?: string; pageName?: string }, docVersionId?: string) {
      const doc = state.documentVersions[docVersionId!];
      addToPagesUsedHelper(params, state.pagesUsed, doc?.nameToPageId);
      return state.pagesUsed;
    },
    getMessagesAndSelected: function getMessagesAndSelectedCB() {
      return { messages: state.messages, selected: state.selected };
    },
    isPublishedOnly: function isPublishedOnlyCB() {
      return state.publishedOnly;
    },
    pagesUsed: function pagesUsedCB() {
      return state.pagesUsed;
    },
    enableNextButton: enableNextButton(),
  };
}
